// Central shared state for STARCODE. Kept intentionally simple (plain object + small helpers)
// rather than pulling in a state-management library - this is a single-page live-visual tool,
// not an app with complex UI state.

export const state = {
  gravityOn: false,
  fireballOn: false,
  sunOn: false,
  ringOn: false,
  rippleOn: false,
  slowMotion: false,
  explosionAudioOn: false,
  morphOn: false,
  morphToggleTime: 0,
  objMorphOn: false,
  objMorphToggleTime: 0,
  loadedObjName: null,
  // Branding morph: spells out STUDIOPESC.COM, mutually exclusive with the other morph targets.
  brandMorphOn: false,
  brandMorphToggleTime: 0,

  // Held (not toggled) negative/positive attractor, exactly like A/S in NEU.pde: force is only
  // applied while the key is physically held down, set directly true/false by keydown/keyup.
  negAttractorHeld: false,
  posAttractorHeld: false,

  // --- Extended effect set, ported from NEU.pde's broader keyboard vocabulary ---
  wellsOn: false,
  galaxySpiralOn: false, // Galaxy 01 - Kepler Differential Spiral
  galaxyDiscOn: false, // Galaxy 02 - 3-Arm Flat Spiral Disc
  cosmicGalaxyOn: false, // Galaxy 03 - Realistic Logarithmic Multi-Arm Spiral Galaxy with Central Bulge & Beat Waves
  galaxy04On: false, // Galaxy 04 - Grand-Design Barred Spiral Galaxy with Central Flare Bar & Starburst Arms
  ringNebulaOn: false, // Ring Nebula (M57) - Glowing Cyan Cavity & Fiery Amber Outer Ionization Ring
  swarmOn: false,
  vortexOn: false,
  blackHoleOn: false,
  neutronStarOn: false,
  liquidRippleOn: false,
  inkBleedOn: false,
  portalOn: false,
  causticsOn: false,
  chaosBoostOn: false,
  distortionOn: false,
  fractalOn: false,
  patternOn: false,
  redCircleOn: false,
  shapeChangeOn: false,
  streamsOn: false,
  starSkyOn: false,
  colorInvertOn: false,
  scalePulseOn: false,
  // --- Restored NEU.pde number-row audio-reactive toggles (previously missing/replaced) ---
  spiralHighOn: false, // Taste 4 - spiral movement driven by high frequencies
  bandExplodeOn: false, // Taste 5 - explosion triggered per audio band (low/mid/high)
  shimmerOn: false, // Taste 7 - shimmer wave + sparkle driven by mid/high frequencies
  complexOn: false, // Taste ß - complex/ripple2 pulsing single-point travel effect
  permanentColor: 0, // 0 off, 1 blue, 2 red

  // --- NEW audio-reactive colour/light effects -------------------------------------------
  // Bioluminescence: light TRAVELS through the cloud as overlapping waves instead of every
  // particle blinking in unison - an organic, deep-sea-creature feel.
  bioluminescenceOn: false,
  // Bioluminescence 2 (Synaptic Neural Photophores): deep-sea dinoflagellate coelenterazine reaction
  // with dendritic pulse propagation and explosive phosphorescence cascade on transients.
  bioSynapseOn: false,
  bioSynapseIntensity: 1.0, // 0..2 - Synaptic photophore flash intensity
  // Each particle is assigned a frequency band and coloured by it (sub=red ... treble=violet),
  // so the swarm literally renders the spectrum.
  spectrumColorOn: false,
  // A hue rainbow that sweeps spatially through the cloud, speed driven by audio.
  chromaWaveOn: false,
  // Colour + brightness flash that fires on detected beats and radiates outward from the centre.
  beatFlashOn: false,
  // Per-particle size driven by audio with a travelling delay (not all growing at once).
  audioSizeOn: false,

  // --- NEW audio-reactive motion effects ---------------------------------------------------
  beatPulseOn: false, // radial shockwave kick on every detected beat
  breathOn: false, // whole cloud inhales/exhales with the bass
  tunnelOn: false, // particles form a travelling tunnel/wormhole down the Z axis
  helixOn: false, // double-helix / DNA strand formation, twist driven by mids
  strobeFieldOn: false, // sharp per-group displacement snaps on transients (spectral flux)
  spectralCathedralOn: false, // five independently breathing architectural frequency arches
  pulseBloomOn: false, // beat-driven twelve-petal positional bloom with spectral twist

  // Toroidal edge wrap, exactly like NEU.pde's update(): a particle leaving one side of the
  // world box immediately re-enters on the opposite side instead of being pushed back in.
  wrapModeOn: false,

  // Rigid, breathing square concentric grid - snaps the swarm onto nested square rings that
  // pulse inward/outward like a heartbeat. Toggled via its control-bar button only.
  gridPulseOn: false,

  // --- Audio-Reactivity Modulation switches ------------------------------------------------
  // Internal master remains enabled; the redundant UI switch was removed.
  audioReactGlobal: true,
  audioReactAttractors: true,
  audioReactBasis: true,
  audioReactEffects: true,
  audioReactExtended: true,

  // --- Output Window Format / Resolution for Resolume --------------------------------------
  outputPreset: "16:9_1080", // default: 16:9 widescreen Full HD
  outputWidth: 1920,
  outputHeight: 1080,
  particleCount: 272144,

  // --- Auto-Mix ("Auto-VJ") ----------------------------------------------------------------
  // Automatically rotates through curated audio-reactive effect combinations, switching on
  // musical boundaries (detected beats) rather than a dumb fixed timer.
  autoMixOn: false,
  autoMixIntensity: 0.5, // 0..1 - how often it switches

  // --- Live / Resolume output mode ----------------------------------------------------------
  // Hides every piece of UI (control bar, HUD, cursor) so the browser window is a clean feed
  // that Resolume Arena can pick up via screen/window capture for projector work.
  liveModeOn: false,

  // --- Global performance parameters, all MIDI-controllable (see midi.js) -------------------
  masterIntensity: 1.0, // 0..2 - global force multiplier for every effect
  sizeBoost: 1.0, // 0..3 - global particle size multiplier
  hueShift: 0.0, // 0..1 - global hue rotation
  bloomAmount: 0.25, // 0..3 - post-processing bloom strength (dezent & knackig)
  speedScale: 0.40, // 0..2 - global simulation speed (standard: 0.40)
  rotationSpeed: 0.35, // 0..3 - auto-rotation speed of the camera
  rotationAudioReact: false, // audio-reactivity toggle for camera rotation speed
  spin360On: false, // continuous 360-degree unconstrained rotation loop
  audioGain: 1.0, // 0..3 - multiplies the analysed audio bands before they drive anything
  bioIntensity: 0.85, // 0..2 - Bioluminescence brightness/calmness slider
  bioSpeed: 0.55, // 0..2 - Bioluminescence travel speed multiplier
  colorWheelActive: false, // interactive color spectrum picker toggle
  focusDistance: 780, // 200..1600 - depth of field camera focal plane distance
  dofBlur: 0.0, // 0..2 - depth of field blur / bokeh strength (default: 0.0 = crisp everywhere)
  dofInvert: false, // false = Weit unscharf (Hintergrund), true = Nah unscharf (Vordergrund)
  dofAudioReact: false, // audio-reactivity toggle for bokeh blur pulsation
  autofocusOn: false, // true = auto-track camera-to-subject focal distance dynamically

  sphereOn: false, // 3D spherical shell formation
  cameraFollowOn: false, // camera automatically centers and follows swarm center of mass
  autoCamOn: false, // automatic Z-axis camera zoom towards particle swarm
  autoCamDistance: 600, // 50..5000 - stroke distance (how far the camera flies forward relative to its starting point)
  autoCamSpeed: 1.0, // 0.1..3.0 - flight velocity multiplier of the AUTOCAM
  audioNoiseGate: 0.04, // 0.00..0.50 - noise gate cutoff threshold to eliminate background hiss/low noise
  shockwaveAudioOn: false, // audio-reactive shockwave mode (triggers & modulates on bass/beats)

  // --- New: Firefly River & VJ Features ---
  fireflyRiverOn: false, // 🌊 Glühwürmchen-Fluss (meandering laminar particle rivers from Processing sketch)
  blackoutActive: false, // ⚡ Instant Blackout / Strobe Killswitch for live drops
  cameraFlightMode: "manual", // "manual" | "drone" | "infinity" | "flythrough"

  // --- 3D Model Morph Deformers (Audio-Reactive OBJ manipulation) ------------------------
  objDef1On: false, // OBJ_DEF 1 - Voxel Elevation & Bass Slicing
  objDef2On: false, // OBJ_DEF 2 - Normal Spikes & Transient Burst
  objDef3On: false, // OBJ_DEF 3 - Harmonic Resonator & Surface Dispersal

  // --- Lightning & Current Effects -------------------------------------------------------
  electroOn: false, // ⚡ Electro - Fast electric current & lightning arc crawling wave

  // --- Layout & Performance Switches ----------------------------------------------------
  viewportTopMode: true, // default on everywhere; only the dedicated Top Viewport button may change it
  muteLocalRender: false, // true = pauses local canvas rendering to conserve GPU/CPU power

  // --- 5 Cosmic & Organic Audio-Reactive Effects -----------------------------------------
  crystalOn: false, // 3D crystalline lattice / facets with audio-reactive lattice pulses
  hexMatrixOn: false, // ⬡ 3D Honeycomb Prism Matrix (Hexagonal crystallographic packing)
  fibonacciGridOn: false, // 🌻 Golden Ratio Phyllotaxis Fibonacci Spiral Shell Lattice
  bioCellOn: false, // Pulsing organic cellular membrane / mitosis flow
  ringNebulaOn: false, // Ring Nebula (M57) - Elliptical ionization gas torus & glowing central cavity

  // --- 6 Forces & Dynamics VJ Effects ----------------------------------------------
  antimatterCollisionOn: false, // Opposing charge collision plane & annihilation sparks
  cryoLatticeOn: false, // Heavy velocity damping with 3D ice lattice freezing
  hyperdriveWarpOn: false, // Z-axis hyperdrive star-streak tunnel acceleration

  sizeMultiplier: 1.0,

  explodeTime: -999,
  shockwaveTime: -999,
  shockwaveStrength: 0,

  // Tracks the most-recently-toggled effect so the MIDI mod wheel (CC7) and the on-screen
  // reactive HUD always know which parameter is "currently selected", mirroring the
  // autoMidiTargetId()/applyMidiControlToSelectedTarget() pattern from NEU.pde.
  lastToggledEffect: null,

  midiValue: 0.5, // 0..1, driven by CC7 (mod wheel / fader) when a MIDI device is connected
  midiConnected: false,

  audioReady: false,
};

const SIMPLE_TOGGLES = {
  gravity: "gravityOn",
  fireball: "sunOn",
  fireballOn: "sunOn",
  sun: "sunOn",
  sunOn: "sunOn",
  sonne: "sunOn",
  ring: "ringOn",
  ripple: "rippleOn",
  slowMotion: "slowMotion",
  explosionAudioOn: "explosionAudioOn",
  wellsOn: "wellsOn",
  galaxyDiscOn: "galaxyDiscOn",
  galaxy02: "galaxyDiscOn",
  galaxy02On: "galaxyDiscOn",
  galaxySpiralOn: "galaxySpiralOn",
  galaxy01: "galaxySpiralOn",
  galaxy01On: "galaxySpiralOn",
  cosmicGalaxy: "cosmicGalaxyOn",
  cosmicGalaxyOn: "cosmicGalaxyOn",
  galaxy03: "cosmicGalaxyOn",
  galaxy03On: "cosmicGalaxyOn",
  galaxy04: "galaxy04On",
  galaxy04On: "galaxy04On",
  ringNebula: "ringNebulaOn",
  ringNebulaOn: "ringNebulaOn",
  m57: "ringNebulaOn",
  swarmOn: "swarmOn",
  vortexOn: "vortexOn",
  blackHole: "blackHoleOn",
  blackHoleOn: "blackHoleOn",
  neutronStar: "neutronStarOn",
  neutronStarOn: "neutronStarOn",
  liquidRippleOn: "liquidRippleOn",
  inkBleedOn: "inkBleedOn",
  portalOn: "portalOn",
  causticsOn: "causticsOn",
  chaosBoostOn: "chaosBoostOn",
  distortionOn: "distortionOn",
  fractalOn: "fractalOn",
  patternOn: "patternOn",
  redCircleOn: "redCircleOn",
  shapeChangeOn: "shapeChangeOn",
  streamsOn: "streamsOn",
  starSkyOn: "starSkyOn",
  colorInvertOn: "colorInvertOn",
  scalePulseOn: "scalePulseOn",
  spiralHighOn: "spiralHighOn",
  bandExplodeOn: "bandExplodeOn",
  shimmerOn: "shimmerOn",
  complexOn: "complexOn",
  bioluminescenceOn: "bioluminescenceOn",
  spectrumColorOn: "spectrumColorOn",
  chromaWaveOn: "chromaWaveOn",
  beatFlashOn: "beatFlashOn",
  audioSizeOn: "audioSizeOn",
  beatPulseOn: "beatPulseOn",
  breathOn: "breathOn",
  tunnelOn: "tunnelOn",
  helixOn: "helixOn",
  strobeFieldOn: "strobeFieldOn",
  spectralCathedralOn: "spectralCathedralOn",
  pulseBloomOn: "pulseBloomOn",
  wrapModeOn: "wrapModeOn",
  autoMixOn: "autoMixOn",
  gridPulseOn: "gridPulseOn",
  bioSynapseOn: "bioSynapseOn",
  bioSynapse: "bioSynapseOn",
  sphereOn: "sphereOn",
  sphere: "sphereOn",
  kugel: "sphereOn",
  spin360On: "spin360On",
  objMorphOn: "objMorphOn",
  dofInvert: "dofInvert",
  dofInvertOn: "dofInvert",
  dofAudioReact: "dofAudioReact",
  dofAudioReactOn: "dofAudioReact",
  rotationAudioReact: "rotationAudioReact",
  rotationAudioReactOn: "rotationAudioReact",
  autofocusOn: "autofocusOn",
  autofocus: "autofocusOn",
  cameraFollowOn: "cameraFollowOn",
  cameraFollow: "cameraFollowOn",
  autoCamOn: "autoCamOn",
  autoCam: "autoCamOn",
  shockwaveAudioOn: "shockwaveAudioOn",
  shockwaveAudio: "shockwaveAudioOn",
  objDef1On: "objDef1On",
  objDef1: "objDef1On",
  objDef2On: "objDef2On",
  objDef2: "objDef2On",
  objDef3On: "objDef3On",
  objDef3: "objDef3On",
  electroOn: "electroOn",
  electro: "electroOn",
  fireflyRiverOn: "fireflyRiverOn",
  fireflyRiver: "fireflyRiverOn",
  fireflyOn: "fireflyRiverOn",
  firefly: "fireflyRiverOn",
  viewportTopMode: "viewportTopMode",
  muteLocalRender: "muteLocalRender",
  crystalOn: "crystalOn",
  crystal: "crystalOn",
  hexMatrixOn: "hexMatrixOn",
  hexMatrix: "hexMatrixOn",
  fibonacciGridOn: "fibonacciGridOn",
  fibonacciGrid: "fibonacciGridOn",
  bioCellOn: "bioCellOn",
  bioCell: "bioCellOn",
  antimatterCollisionOn: "antimatterCollisionOn",
  antimatterCollision: "antimatterCollisionOn",
  cryoLatticeOn: "cryoLatticeOn",
  cryoLattice: "cryoLatticeOn",
  hyperdriveWarpOn: "hyperdriveWarpOn",
  hyperdriveWarp: "hyperdriveWarpOn",
  audioReactGlobal: "audioReactGlobal",
  audioReactAttractors: "audioReactAttractors",
  audioReactBasis: "audioReactBasis",
  audioReactEffects: "audioReactEffects",
  audioReactExtended: "audioReactExtended",
};

export function toggleEffect(name) {
  if (name === "morph") {
    state.morphOn = !state.morphOn;
    if (state.morphOn) { state.objMorphOn = false; state.brandMorphOn = false; }
    state.morphToggleTime = performance.now() / 1000;
    state.lastToggledEffect = name;
    return;
  }
  if (name === "objMorph" || name === "objMorphOn") {
    state.objMorphOn = !state.objMorphOn;
    if (state.objMorphOn) { state.morphOn = false; state.brandMorphOn = false; }
    state.objMorphToggleTime = performance.now() / 1000;
    state.lastToggledEffect = name;
    return;
  }
  if (name === "brandMorph" || name === "brandMorphOn") {
    state.brandMorphOn = !state.brandMorphOn;
    if (state.brandMorphOn) { state.morphOn = false; state.objMorphOn = false; }
    state.brandMorphToggleTime = performance.now() / 1000;
    state.lastToggledEffect = name;
    return;
  }
  const field = SIMPLE_TOGGLES[name];
  if (!field) return;
  state[field] = !state[field];
  state.lastToggledEffect = name;
}

export function setPermanentColor(mode) {
  // 0 off, 1 blue, 2 red - mutually exclusive, matches Cmd+Shift+D / Cmd+Shift+R in NEU.pde.
  state.permanentColor = state.permanentColor === mode ? 0 : mode;
}

export function resetEffects() {
  state.gravityOn = false;
  state.fireballOn = false;
  state.sunOn = false;
  state.ringOn = false;
  state.rippleOn = false;
  state.slowMotion = false;
  state.explosionAudioOn = false;
  state.morphOn = false;
  state.morphToggleTime = 0;
  state.objMorphToggleTime = 0;
  state.brandMorphToggleTime = 0;
  state.wellsOn = false;
  state.galaxyDiscOn = false;
  state.swarmOn = false;
  state.vortexOn = false;
  state.galaxySpiralOn = false;
  state.galaxyDiscOn = false;
  state.cosmicGalaxyOn = false;
  state.galaxy04On = false;
  state.ringNebulaOn = false;
  state.blackHoleOn = false;
  state.neutronStarOn = false;
  state.liquidRippleOn = false;
  state.inkBleedOn = false;
  state.portalOn = false;
  state.causticsOn = false;
  state.chaosBoostOn = false;
  state.distortionOn = false;
  state.fractalOn = false;
  state.patternOn = false;
  state.redCircleOn = false;
  state.shapeChangeOn = false;
  state.streamsOn = false;
  state.starSkyOn = false;
  state.colorInvertOn = false;
  state.scalePulseOn = false;
  state.spiralHighOn = false;
  state.bandExplodeOn = false;
  state.shimmerOn = false;
  state.complexOn = false;
  state.bioluminescenceOn = false;
  state.spectrumColorOn = false;
  state.chromaWaveOn = false;
  state.beatFlashOn = false;
  state.audioSizeOn = false;
  state.beatPulseOn = false;
  state.breathOn = false;
  state.tunnelOn = false;
  state.helixOn = false;
  state.strobeFieldOn = false;
  state.spectralCathedralOn = false;
  state.pulseBloomOn = false;
  state.gridPulseOn = false;
  state.bioSynapseOn = false;
  state.bioSynapseIntensity = 1.0;
  state.sphereOn = false;
  state.spin360On = false;
  state.cameraFollowOn = false;
  state.autoCamOn = false;
  state.shockwaveAudioOn = false;
  state.objDef1On = false;
  state.objDef2On = false;
  state.objDef3On = false;
  state.electroOn = false;
  state.fireflyRiverOn = false;
  state.crystalOn = false;
  state.hexMatrixOn = false;
  state.fibonacciGridOn = false;
  state.bioCellOn = false;
  state.antimatterCollisionOn = false;
  state.cryoLatticeOn = false;
  state.hyperdriveWarpOn = false;
  state.objMorphOn = false;
  state.brandMorphOn = false;
  state.wrapModeOn = false;
  state.audioReactGlobal = true;
  state.audioReactAttractors = true;
  state.audioReactBasis = true;
  state.audioReactEffects = true;
  state.audioReactExtended = true;
  state.negAttractorHeld = false;
  state.posAttractorHeld = false;
  state.permanentColor = 0;
  state.sizeMultiplier = 1.0;
  state.hueShift = 0.0;
  state.masterIntensity = 1.0;
  state.sizeBoost = 1.0;
  state.bloomAmount = 0.25;
  state.speedScale = 0.40;
  state.rotationSpeed = 0.35;
  state.rotationAudioReact = false;
  state.audioGain = 1.0;
  state.bioIntensity = 0.85;
  state.focusDistance = 780;
  state.dofBlur = 0.0;
  state.dofInvert = false;
  state.dofAudioReact = false;
  state.autofocusOn = false;
  state.colorWheelActive = false;
  state.autoMixOn = false;
  state.autoMixIntensity = 0.5;
  state.blackoutActive = false;
  state.muteLocalRender = false;
  state.cameraFlightMode = "manual";
  state.bioSpeed = 0.55;
  state.autoCamDistance = 600;
  state.autoCamSpeed = 1.0;
  state.audioNoiseGate = 0.04;
  state.explodeTime = -999;
  state.shockwaveTime = -999;
  state.shockwaveStrength = 0;
  state.lastToggledEffect = null;
  state.midiValue = 0.5;
}
